import React from 'react';
import './ReturnsCard.scss';
import { NSCard, NSInput } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { numeralFormatter, bpsFormatter } from 'ns_libs/formatter';
import { TargetReturnsValues } from '../DealReturns';
import { DealReturnsUnits } from '../constants';

export interface TargetReturnCardProps {
    isEditing: boolean;
    hasRange?: boolean;
    title: string;
    field?: keyof TargetReturnsValues;
    value: number | null;
    min?: number;
    max?: number;
    unit: DealReturnsUnits;
    handleInputChange?: (field: keyof TargetReturnsValues, value: number) => void;
}

const TargetReturnCard: React.FC<TargetReturnCardProps> = ({
    isEditing = false, title, field, value, min = 0, max = 100, unit, handleInputChange, hasRange = true,
}) => {
    const formattedValue = value !== null ? numeralFormatter(value) : '';
    let displayedValue = '';

    if (formattedValue) {
        if (unit === DealReturnsUnits.BPS) {
            displayedValue = bpsFormatter(value!);
        } else if (unit === DealReturnsUnits.PERCENT || unit === DealReturnsUnits.X) {
            displayedValue = `${formattedValue}${unit}`;
        } else if (unit === DealReturnsUnits.DOLLAR) {
            displayedValue = `${unit}${formattedValue}`;
        }
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (handleInputChange && field) {
            let value = Math.max(event.target.valueAsNumber, min);
            if (max !== undefined) {
                value = Math.min(value, max);
            }
            handleInputChange(field, value);
        }
    };

    const handleBlur = () => {
        if (formattedValue === 0 && handleInputChange && field) {
            handleInputChange(field, 0);
        }
    };

    return (
        <NSCard className="NSCard--level-3 d-flex flex-grow-1 p-3 rounded h-100">
            <h5 className="TargetReturnCard__title m-0 mb-1 font-14">{title}</h5>
            <RenderIf isTrue={isEditing}>
                <NSInput
                    type="number"
                    name={field || ''}
                    value={value}
                    min={min}
                    max={max}
                    step={1}
                    onChange={onChange}
                    inputClassName="mb-2"
                    includeRange={hasRange}
                    prependInputAddon={unit === '$' ? unit : null}
                    appendInputAddon={unit !== '$' ? unit : null}
                    selectOnFocus
                    onBlur={handleBlur}
                />
                <RenderIf isTrue={!hasRange}>
                    <span className="ReturnsCard__no-range-filler" />
                </RenderIf>
            </RenderIf>
            <RenderIf isTrue={!isEditing}>
                <RenderIf isTrue={value !== null}>
                    <h3 className="text-white m-0">{displayedValue}</h3>
                </RenderIf>
                <RenderIf isTrue={value === null && handleInputChange}>
                    <h5 className="text-white m-0 font-12">Not yet entered.</h5>
                </RenderIf>
                <RenderIf isTrue={value === null && !handleInputChange}>
                    <h5 className="text-white m-0 font-12">--</h5>
                </RenderIf>
            </RenderIf>
        </NSCard>
    );
};

export default TargetReturnCard;
